// Itens com detalhes (ORCA-1926 / ORCA-1937). Mesma regra da API
// (app/Services/Proposal/ItemDetails/ProposalItemDetailsService.php): mantenha as duas iguais.

export type ItemDetailsField = 'image' | 'description' | 'category' | 'unit' | 'unit_price' | 'code';
export type ItemDetailsMode = 'default' | 'custom' | 'none';

export interface ItemDetailsDefaults {
    show: boolean;
    group_by_category: boolean;
    fields: Record<ItemDetailsField, boolean>;
}

export const ITEM_DETAILS_FIELDS: ItemDetailsField[] = ['image', 'description', 'category', 'unit', 'unit_price', 'code'];

/** Desmarcados, somem também do editor do item */
export const ITEM_DETAILS_EDITOR_FIELDS: ItemDetailsField[] = ['image', 'description', 'category', 'code'];

/**
 * ORCA-1945 (revisão): Unidade e Preço unitário são obrigatórios e sempre aparecem (no editor, no link e no PDF).
 * Para esconder valores, só a opção "Ocultar valor dos itens", fora de "Detalhes dos itens".
 */
export const ITEM_DETAILS_REQUIRED_FIELDS: ItemDetailsField[] = ['unit', 'unit_price'];

export const ITEM_DETAILS_REQUIRED_HINT = 'Para esconder os valores, use “Ocultar valor dos itens”.';

function withRequired<T extends Record<ItemDetailsField, boolean>>(fields: T): T {
    ITEM_DETAILS_REQUIRED_FIELDS.forEach((f) => (fields[f] = true));
    return fields;
}

export const ITEM_DETAILS_LABELS: Record<ItemDetailsField | 'title', string> = {
    image: 'Imagem do produto',
    title: 'Título',
    description: 'Descrição',
    category: 'Categoria',
    unit: 'Unidade',
    unit_price: 'Preço unitário',
    code: 'Código',
};

/** Rótulos curtos para o resumo "Mostra: imagem, descrição…" */
const SHORT_LABELS: Record<ItemDetailsField, string> = {
    image: 'imagem',
    description: 'descrição',
    category: 'categoria',
    unit: 'unidade',
    unit_price: 'preço unitário',
    code: 'código',
};

// ORCA-1945 (revisão): conta nova começa só com os campos obrigatórios (Título, Unidade, Quantidade e
// Valor unitário), mais perto do uso da versão anterior. Mesmo padrão em ProposalItemDetailsService::SYSTEM_DEFAULT.
export const ITEM_DETAILS_SYSTEM_DEFAULT: ItemDetailsDefaults = {
    show: false,
    group_by_category: false,
    fields: { image: false, description: false, category: false, unit: true, unit_price: true, code: false },
};

/** Estrutura de details_config da API (Item::DETAILS_CONFIG_STRUCTURE) */
const CONFIG_ORDER: (ItemDetailsField | 'hide_item' | 'title')[] = ['hide_item', 'image', 'title', 'description', 'category', 'unit', 'unit_price', 'code'];
const CONFIG_LABELS: Record<string, string> = {
    hide_item: 'Não exibir detalhes deste item',
    ...ITEM_DETAILS_LABELS,
};

function bool(value: any): boolean {
    if (typeof value === 'string') {
        return value === 'true' || value === '1';
    }
    return !!value;
}

export function normalizeItemDetails(value: any): ItemDetailsDefaults {
    const fields = (value && typeof value.fields === 'object' && value.fields) || {};
    const result: ItemDetailsDefaults = {
        show: value?.show === undefined ? ITEM_DETAILS_SYSTEM_DEFAULT.show : bool(value.show),
        group_by_category: bool(value?.group_by_category ?? false),
        fields: { ...ITEM_DETAILS_SYSTEM_DEFAULT.fields },
    };
    ITEM_DETAILS_FIELDS.forEach((f) => {
        if (fields[f] !== undefined) {
            result.fields[f] = bool(fields[f]);
        }
    });
    withRequired(result.fields);
    return result;
}

export function normalizeMode(mode: any): ItemDetailsMode {
    return mode === 'custom' || mode === 'none' ? mode : 'default';
}

export function fieldsFromConfig(config: any[] | null | undefined): Record<ItemDetailsField, boolean> {
    const fields = { image: false, description: false, category: false, unit: false, unit_price: false, code: false };
    (config || []).forEach((entry: any) => {
        if (ITEM_DETAILS_FIELDS.includes(entry?.field)) {
            fields[entry.field as ItemDetailsField] = bool(entry.value);
        }
    });
    return withRequired(fields);
}

export function configFromFields(fields: Partial<Record<ItemDetailsField, boolean>>, hide = false): { field: string; label: string; value: boolean }[] {
    return CONFIG_ORDER.map((field) => ({
        field,
        label: CONFIG_LABELS[field],
        value: field === 'hide_item' ? hide
            : (field === 'title' || ITEM_DETAILS_REQUIRED_FIELDS.includes(field as ItemDetailsField)) ? true
            : !!(fields as any)[field],
    }));
}

export interface ItemDetailsSource {
    product_image?: string | null;
    product_description?: string | null;
    product_category_name?: string | null;
    product_code?: string | null;
    details_mode?: string | null;
    details_config?: any[] | null;
}

/** Texto puro da descrição (o cadastro de produtos guarda HTML) */
export function plainDescription(html: string | null | undefined): string {
    if (!html) {
        return '';
    }
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent || '').replace(/\s+/g, ' ').trim();
}

/** Campos configurados para o item (sem olhar se o item tem o dado) */
export function configuredFields(defaults: ItemDetailsDefaults, item: ItemDetailsSource): Record<ItemDetailsField, boolean> {
    const mode = normalizeMode(item.details_mode);
    // Unidade e preço unitário sempre (mesmo sem detalhes)
    const none = { image: false, description: false, category: false, unit: true, unit_price: true, code: false };
    if (!defaults.show || mode === 'none') {
        return none;
    }
    return withRequired(mode === 'custom' ? fieldsFromConfig(item.details_config) : { ...defaults.fields });
}

/** Campos visíveis no orçamento: configurados e com dado no item */
export function visibleFields(defaults: ItemDetailsDefaults, item: ItemDetailsSource): Record<ItemDetailsField, boolean> {
    const f = configuredFields(defaults, item);
    return {
        ...f,
        image: f.image && !!item.product_image,
        description: f.description && plainDescription(item.product_description) !== '',
        category: f.category && !!(item.product_category_name || '').trim(),
        code: f.code && !!(item.product_code || '').trim(),
    };
}

/** "Mostra: imagem, descrição e categoria" / "Só nome, quantidade e valores" */
export function detailsSummary(defaults: ItemDetailsDefaults, item: ItemDetailsSource): string {
    const f = configuredFields(defaults, item);
    // Unidade e preço unitário são fixos: o resumo fala só dos detalhes opcionais
    const on = ITEM_DETAILS_EDITOR_FIELDS.filter((k) => f[k]).map((k) => SHORT_LABELS[k]);
    if (!on.length) {
        return 'Só nome, quantidade e valores';
    }
    const list = on.length > 1 ? `${on.slice(0, -1).join(', ')} e ${on[on.length - 1]}` : on[0];
    return `Mostra: ${list}`;
}

/** Texto comparável: sem acento e sem diferença de maiúsculas */
export function foldText(value: string | null | undefined): string {
    return (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

/**
 * ORCA-1945: sugestões do campo Categoria. Junta as categorias da conta e as já usadas no orçamento,
 * ignora acento/maiúsculas, mostra primeiro as que começam com o texto e, quando o campo já tem uma
 * categoria escolhida, lista todas (antes só aparecia a própria, e não dava para trocar sem apagar).
 */
export function filterCategories(sources: string[], query: string | null | undefined, limit = 30): string[] {
    const seen = new Set<string>();
    const all: string[] = [];
    (sources || []).forEach((name) => {
        const clean = (name || '').trim();
        const key = foldText(clean);
        if (clean && !seen.has(key)) {
            seen.add(key);
            all.push(clean);
        }
    });
    all.sort((a, b) => a.localeCompare(b, 'pt-BR'));

    const q = foldText(query);
    if (!q || all.some((name) => foldText(name) === q)) {
        return all.slice(0, limit);
    }
    const starts = all.filter((name) => foldText(name).startsWith(q));
    const contains = all.filter((name) => !foldText(name).startsWith(q) && foldText(name).includes(q));
    return [...starts, ...contains].slice(0, limit);
}

/** A categoria digitada ainda não existe (vai ser criada ao salvar no cadastro) */
export function isNewCategory(sources: string[], query: string | null | undefined): boolean {
    const q = foldText(query);
    return !!q && !(sources || []).some((name) => foldText(name) === q);
}

export interface ProductStockInfo {
    label: string;
    tone: 'ok' | 'warning' | 'danger';
}

/**
 * ORCA-1945 (revisão): estoque do produto na busca de itens, quando o controle de estoque está ligado
 * (na conta e no produto). "Estoque: 12 un" · baixo (≤ mínimo, com alerta ligado) · "Sem estoque".
 */
export function productStockInfo(
    product: { stockQuantity?: number | null; stockControlEnabled?: boolean | null; estoqueMinimo?: number | null } | null | undefined,
    options: { enabled: boolean; minAlert?: boolean; unitAbbr?: string | null },
): ProductStockInfo | null {
    if (!product || !options.enabled || product.stockControlEnabled === false) {
        return null;
    }
    const qty = Number(product.stockQuantity ?? 0) || 0;
    if (qty <= 0) {
        return { label: 'Sem estoque', tone: 'danger' };
    }
    const amount = qty.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
    const label = `Estoque: ${amount}${options.unitAbbr ? ' ' + options.unitAbbr.toLowerCase() : ''}`;
    const min = product.estoqueMinimo;
    const low = !!options.minAlert && min !== null && min !== undefined && qty <= Number(min);
    return { label: low ? `${label} (baixo)` : label, tone: low ? 'warning' : 'ok' };
}
